/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import {
  array,
  fragment,
  fragmentArray,
} from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import type ReportStudentNameFormat from '@linc-technologies/linced-common/enums/reports/report-student-name-format';
import type ReportTemplateTheme from '@linc-technologies/linced-common/enums/reports/report-template-theme';
import type ReportTemplateType from '@linc-technologies/linced-common/enums/reports/report-template-type';
import type ReportJudgementWording from '@linc-technologies/linced-common/models/reports/report-judgement-wording';
import type ReportTemplatePeriod from '@linc-technologies/linced-common/models/reports/report-template-period';
import type School from '@linc-technologies/linced-common/models/school';

// Proto: reports.ReportTemplate.
export default class ReportTemplate extends Model {
  declare [Type]: 'reports/report-template';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @attr('string', { defaultValue: '' })
  declare name: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @attr('number', { defaultValue: 0 })
  declare type: EnumValue<typeof ReportTemplateType>;

  @array('string')
  declare accessRoles: StatefulArray<string>;

  @fragmentArray('reports/report-template-period')
  declare periods: FragmentArray<ReportTemplatePeriod>;

  // Wording for the bold opening line of generated teacher comments
  // (reports/report-judgement-wording); empty phrases use the defaults. Null
  // when the backend leaves it out.
  @fragment('reports/report-judgement-wording')
  declare judgementWording: ReportJudgementWording | null;

  @attr('boolean', { defaultValue: false })
  declare isActive: boolean;

  @attr('number', { defaultValue: 0 })
  declare validFrom: number;

  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  @attr('boolean', { defaultValue: false })
  declare isGlobal: boolean;

  @attr('string', { defaultValue: '' })
  declare next: string;

  @attr('number', { defaultValue: 0 })
  declare theme: EnumValue<typeof ReportTemplateTheme>;

  // Which of the learner's first and last names the report shows. 0
  // (Unspecified) means a template saved before these existed; see
  // resolveNameFormat in utils/report-student-name.
  @attr('number', { defaultValue: 0 })
  declare studentFirstNameFormat: EnumValue<typeof ReportStudentNameFormat>;

  @attr('number', { defaultValue: 0 })
  declare studentLastNameFormat: EnumValue<typeof ReportStudentNameFormat>;

  @attr('string', { defaultValue: '' })
  declare globalId: string;

  // Epoch seconds when the template was soft-deleted; 0/empty when not
  // deleted. Soft-deleted templates are excluded from list queries but
  // remain retrievable by id (and via the LINC-ED-only deleted list).
  @attr('number')
  declare deleteTime: number | undefined;

  // Id of the user who created the template (set server-side from the JWT).
  @attr('string', { defaultValue: '' })
  declare authorId: string;

  // Id of the user who soft-deleted the template (set server-side from the
  // JWT on delete, cleared on restore); empty when not deleted.
  @attr('string', { defaultValue: '' })
  declare deletedBy: string;
}
